import { useEffect, useState } from "react";
import { CalendarDays, FileText, GraduationCap, HandCoins, ClipboardCheck, MessageSquare, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { TabKicker, TabSectionTitle } from "./_shell";

/**
 * ApplicationTimeline — horizontal 12-month calendar of the key
 * application milestones for one uni, sourced from
 * `GET /universities/{id}/application-timeline`.
 *
 * The horizontal rail lays out months Sept → Aug (UK cycle) or the
 * country equivalent. Each stage lands at its calendar month with a
 * coloured chip; hovering / focusing surfaces the detail copy.
 *
 * Kinds → icon + colour:
 *   register / apply → navy (#002147)
 *   test             → purple (#7C3AED)
 *   interview        → amber  (#B45309)
 *   offer            → emerald (#059669)
 *   deposit          → rose   (#9F1239)
 *   matriculate      → teal   (#0F766E)
 */

const KIND_META = {
  register:    { colour: "#002147", Icon: FileText },
  apply:       { colour: "#002147", Icon: FileText },
  test:        { colour: "#7C3AED", Icon: ClipboardCheck },
  interview:   { colour: "#B45309", Icon: MessageSquare },
  offer:       { colour: "#059669", Icon: Sparkles },
  deposit:     { colour: "#9F1239", Icon: HandCoins },
  matriculate: { colour: "#0F766E", Icon: GraduationCap },
};

const MONTHS_UK_CYCLE = [
  { m: 9,  name: "SEP" },
  { m: 10, name: "OCT" },
  { m: 11, name: "NOV" },
  { m: 12, name: "DEC" },
  { m: 1,  name: "JAN" },
  { m: 2,  name: "FEB" },
  { m: 3,  name: "MAR" },
  { m: 4,  name: "APR" },
  { m: 5,  name: "MAY" },
  { m: 6,  name: "JUN" },
  { m: 7,  name: "JUL" },
  { m: 8,  name: "AUG" },
];

export default function ApplicationTimeline({ uniId }) {
  const [stages, setStages] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uniId}/application-timeline`)
      .then((r) => { if (!cancelled) setStages(r.data?.stages || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uniId]);

  if (!stages || stages.length === 0) return null;

  // Group stages by their cycle month so simultaneous entries stack.
  const cycleMonth = (m) => (m >= 8 ? m : m + 12);
  const byMonth = {};
  for (const s of stages) {
    const cm = cycleMonth(s.month);
    (byMonth[cm] = byMonth[cm] || []).push(s);
  }

  return (
    <section className="mb-8" data-testid="application-timeline">
      <div>
        <TabKicker>01 · Application Timeline</TabKicker>
        <TabSectionTitle>Every deadline that matters, in one glance</TabSectionTitle>
      </div>

      <div className="border border-[#002147]/10 bg-white/40 p-4 md:p-6 relative overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Month axis */}
          <div className="grid grid-cols-12 border-b border-[#002147]/10 pb-2 mb-4">
            {MONTHS_UK_CYCLE.map((mo) => (
              <div key={mo.m} className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60 text-center">
                {mo.name}
              </div>
            ))}
          </div>
          {/* Timeline rail */}
          <div className="grid grid-cols-12 relative">
            {MONTHS_UK_CYCLE.map((mo, i) => {
              const cm = cycleMonth(mo.m);
              const items = byMonth[cm] || [];
              return (
                <div key={mo.m}
                     className={`relative border-l ${i === 0 ? "" : "border-[#002147]/5"} px-1.5 min-h-[140px]`}>
                  {items.map((s, j) => {
                    const meta = KIND_META[s.kind] || KIND_META.apply;
                    const Icon = meta.Icon;
                    return (
                      <div
                        key={j}
                        className="mb-2.5 rounded-sm bg-white border border-[#002147]/10 p-2 hover:shadow-sm hover:border-[#002147]/30 transition-all cursor-help"
                        title={s.detail}
                        data-testid={`timeline-stage-${s.kind}-${s.date}`}
                      >
                        <div className="flex items-center gap-1.5 pb-1 border-b border-[#002147]/10 mb-1.5"
                             style={{ borderBottomColor: `${meta.colour}30` }}>
                          <Icon className="h-3 w-3 shrink-0" style={{ color: meta.colour }} />
                          <span className="font-mono text-[9px] uppercase tracking-widest font-semibold"
                                style={{ color: meta.colour }}>
                            {mo.name} {s.day.toString().padStart(2, "0")}
                          </span>
                        </div>
                        <div className="font-sans text-[11px] font-semibold text-[#002147] leading-tight">
                          {s.title}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-3 font-mono text-[10px] uppercase tracking-wider text-[#002147]/60">
        {Object.entries(KIND_META).map(([k, meta]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="inline-block h-0.5 w-4" style={{ backgroundColor: meta.colour }} />
            {k}
          </span>
        ))}
      </div>
    </section>
  );
}
